import { format, subHours } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import {
  DSAttendanceAuditType,
  DS_ATTENDANCE_AUDIT_TYPE_LABEL
} from '@/constants/ds-attendance.constants'
import { manualReviewReasonLabel } from '@/constants/ds-attendance-auto-approval.constants'
import { formatTimeInZone } from '@/modules/company/Approvals/DsAttendance/utils/leg-overwrite'

// Evento de histórico exibido no DsAttendanceHistoryCard. Definido aqui (e não
// importado do SFC) porque um módulo .ts não enxerga exports nomeados de um .vue
// (o shim de *.vue só expõe o default). É estruturalmente idêntico ao HistoryEvent
// do card, então o retorno permanece atribuível aos refs `ref<HistoryEvent[]>`.
/** Spec 2026-09-24 §7 — link para o registro contraparte de uma sobrescrita de perna. */
export interface HistoryEventLink {
  label: string
  containerId: number
}

export interface HistoryEvent {
  date: string
  description: string
  time: string
  isPrimary?: boolean
  links?: HistoryEventLink[]
}

// Mapeia um item da trilha de auditoria de um container de ds-attendance para um
// evento de histórico. Extraído (R2) de cópias byte-idênticas em
// stationDetailsModal.vue, DsAttendanceAwaitingShipment.vue e DsAttendanceAwaitingAudit.vue.
/** Forma mínima de um item da trilha consumida aqui; `newValues` refina labels (fase 1). */
export interface AuditTrailItemLike {
  actionType: string
  updaterUser: { name: string } | null
  justification: string | null
  createdAt: string
  newValues?: Record<string, unknown> | null
  oldValues?: Record<string, unknown> | null
}

/* ------------------------------------------------------------------------- *
 * Spec 2026-09-24 §7 — sobrescrita de perna. `newValues.legs` traz
 * `{ leg, decision, before: {containerId, kind, time}, after: {...} }`.
 * ------------------------------------------------------------------------- */

const LEG_OVERWRITE_TYPES: readonly string[] = [
  DSAttendanceAuditType.LEGS_OVERWRITTEN,
  DSAttendanceAuditType.LEGS_OVERWRITTEN_BY,
  DSAttendanceAuditType.LEGS_OVERWRITE_KEPT,
  DSAttendanceAuditType.LEGS_OVERWRITE_UNDONE
]

interface LegAuditSide {
  containerId?: number | null
  kind?: string | null
  time?: string | null
}

interface LegAuditItem {
  leg?: string
  decision?: string
  before?: LegAuditSide | null
  after?: LegAuditSide | null
}

const LEG_WORDS: Record<string, string> = { CHECKIN: 'check-in', CHECKOUT: 'check-out' }

function positiveId(value: unknown): number | null {
  const id = Number(value)
  return Number.isInteger(id) && id > 0 ? id : null
}

function legItems(newValues: Record<string, unknown> | null | undefined): LegAuditItem[] {
  const legs = newValues?.legs
  return Array.isArray(legs) ? (legs as LegAuditItem[]) : []
}

function legWord(item: LegAuditItem): string {
  return (item.leg && LEG_WORDS[item.leg]) || item.leg || 'perna'
}

function legNames(items: LegAuditItem[]): string {
  return items.map(legWord).join(' e ')
}

function refLabel(id: unknown): string {
  const valid = positiveId(id)
  return valid ? `#${valid}` : 'outro registro'
}

/** `newValues.timezone` = offset do plantão gravado pelo backend (`-03:00`); ausente = `-03:00`. */
function shortTime(iso: string | null | undefined, timeZone: unknown): string {
  return formatTimeInZone(iso, typeof timeZone === 'string' ? timeZone : null) ?? ''
}

function timeShift(item: LegAuditItem, timeZone: unknown): string {
  const before = shortTime(item.before?.time, timeZone)
  const after = shortTime(item.after?.time, timeZone)
  return before && after && before !== after ? `, ${before} → ${after}` : ''
}

function describeOverwrittenLeg(item: LegAuditItem, timeZone: unknown): string {
  const from = refLabel(item.before?.containerId)
  if (item.decision === 'KEEP') return `${legWord(item)} mantido em ${from}`
  return `${legWord(item)} passa a valer por este registro (antes ${from}${timeShift(item, timeZone)})`
}

/** `check-in 07:00 → 07:12` (ou `, mesmo horário`); vazio quando o payload não traz os horários. */
function legBeforeAfter(item: LegAuditItem, timeZone: unknown): string {
  const before = shortTime(item.before?.time, timeZone)
  const after = shortTime(item.after?.time, timeZone)
  if (!before || !after) return ''
  const times = before === after ? `${before}, mesmo horário` : `${before} → ${after}`
  return `${legWord(item)} ${times}`
}

/** Horários por perna entre parênteses; payload antigo (sem horários) cai no `fallback`. */
function legTimesSuffix(legs: LegAuditItem[], timeZone: unknown, fallback: string): string {
  const parts = legs.map((leg) => legBeforeAfter(leg, timeZone))
  if (parts.length && parts.every(Boolean)) return ` (${parts.join('; ')})`
  return fallback ? ` (${fallback})` : ''
}

function describeOverwritten(newValues: Record<string, unknown>, head: string): string {
  const parts = legItems(newValues)
    .map((leg) => describeOverwrittenLeg(leg, newValues.timezone))
    .join('; ')
  return parts ? `${head} — ${parts}` : head
}

function describeOverwrittenBy(newValues: Record<string, unknown>, head: string): string {
  const legs = legItems(newValues)
  const verb = legs.length > 1 ? 'passam' : 'passa'
  const times = legTimesSuffix(legs, newValues.timezone, '')
  return `${head} — ${legNames(legs) || 'perna'} ${verb} a valer por ${refLabel(newValues.byContainerId)}${times}`
}

function describeUndone(newValues: Record<string, unknown>, label: string): string {
  const legs = legItems(newValues)
  const suffix = legTimesSuffix(legs, newValues.timezone, legNames(legs))
  return `${label} — reversão de ${refLabel(newValues.revertedContainerId)}${suffix}`
}

function refineLegOverwriteLabel(item: AuditTrailItemLike, label: string): string {
  const newValues = item.newValues ?? {}
  const head = newValues.automatic === true ? `${label} (automática)` : label
  switch (item.actionType) {
    case DSAttendanceAuditType.LEGS_OVERWRITTEN:
      return describeOverwritten(newValues, head)
    case DSAttendanceAuditType.LEGS_OVERWRITTEN_BY:
      return describeOverwrittenBy(newValues, head)
    case DSAttendanceAuditType.LEGS_OVERWRITE_KEPT:
      return `${label} — ${legNames(legItems(newValues)) || 'perna'} continua com este registro na aprovação de ${refLabel(newValues.byContainerId)}`
    case DSAttendanceAuditType.LEGS_OVERWRITE_UNDONE:
      return describeUndone(newValues, label)
    default:
      return label
  }
}

/** Contrapartes linkáveis do evento (§7, Q14). Vazio para os tipos que não são de sobrescrita. */
export function counterpartLinks(item: AuditTrailItemLike): HistoryEventLink[] {
  if (!LEG_OVERWRITE_TYPES.includes(item.actionType)) return []
  const newValues = item.newValues ?? {}
  const ids = new Set<number>()
  const add = (value: unknown) => {
    const id = positiveId(value)
    if (id) ids.add(id)
  }
  if (item.actionType === DSAttendanceAuditType.LEGS_OVERWRITTEN) {
    const counterparts = newValues.counterpartContainerIds
    if (Array.isArray(counterparts)) counterparts.forEach(add)
    else legItems(newValues).forEach((leg) => add(leg.before?.containerId))
  }
  add(newValues.byContainerId)
  add(newValues.revertedContainerId)
  return [...ids].map((containerId) => ({ containerId, label: `Abrir registro #${containerId}` }))
}

/**
 * Fase 1 do fluxo de auditoria (spec 2026-09-11, §5.3) — "Linhas pré-preenchidas com a
 * escala" seguido de nada dizia ao auditor que o espelho tinha falhado. Quando o prefill
 * gravou 0 linhas COM motivo, a trilha passa a dizer por quê.
 *
 * `createdCount` ausente NÃO vira 0: backend anterior à fase 1 não grava o campo, e o label
 * clássico continua valendo.
 */
function refineAuditLabel(item: AuditTrailItemLike, label: string): string {
  // O backend grava o mesmo APPROVAL_REVERTED ao reverter aprovação e reprovação.
  if (item.actionType === DSAttendanceAuditType.APPROVAL_REVERTED) {
    const wasRejection =
      item.newValues?.status === 'PENDING_AUDIT' || item.oldValues?.status === 'REJECTED'
    return wasRejection ? 'Reprovação revertida' : label
  }
  if (LEG_OVERWRITE_TYPES.includes(item.actionType)) return refineLegOverwriteLabel(item, label)
  if (item.actionType === DSAttendanceAuditType.CONTAINER_MANUAL_REVIEW_REQUIRED)
    return refineManualReviewLabel(item, label)
  if (item.actionType !== DSAttendanceAuditType.CONTAINER_ENTRIES_PREFILLED) return label
  const newValues = item.newValues
  if (!newValues) return label
  if (newValues.createdCount !== 0) return label
  if (!newValues.emptyReason) return label
  return 'Linhas pré-preenchidas com a escala (0 — sem plantão previsto)'
}

/**
 * Fase 2 (spec 2026-09-11 §6.3) — "Enviado para revisão manual" sem o motivo não diz nada:
 * o auditor precisa saber se foi horário fora da tolerância, linha ausente ou amostragem.
 * Motivo desconhecido entra cru (o backend pode publicar um motivo novo antes do web),
 * e a ausência de motivo mantém o label clássico.
 */
function refineManualReviewLabel(item: AuditTrailItemLike, label: string): string {
  const reason = item.newValues?.reason
  if (typeof reason !== 'string' || reason === '') return label
  return `${label} — ${manualReviewReasonLabel(reason)}`
}

export function mapAuditToHistoryEvent(item: AuditTrailItemLike, index: number): HistoryEvent {
  // API retorna createdAt em UTC sem indicador de timezone; subtrai 3h para converter a UTC-3 (Brasília)
  const date = subHours(new Date(item.createdAt), 3)
  const baseLabel =
    DS_ATTENDANCE_AUDIT_TYPE_LABEL[item.actionType as DSAttendanceAuditType] ?? item.actionType
  const label = refineAuditLabel(item, baseLabel)
  const description = item.updaterUser ? `${label} — ${item.updaterUser.name}` : label
  const links = counterpartLinks(item)
  return {
    date: format(date, 'dd/MM/yyyy', { locale: ptBR }),
    description: item.justification ? `${description}: ${item.justification}` : description,
    time: format(date, "HH:mm 'UTC-3'"),
    isPrimary: index === 0,
    ...(links.length ? { links } : {})
  }
}

/**
 * BUG-03 — `ENTRY_SHADOW_SCORE` (fase 29/ESP-03) é telemetria interna do shadow mode:
 * gravada a cada conciliação, mas por definição não é uma decisão visível ao operador.
 * Nenhum outro tipo é escondido — apenas os que a auditoria interna decidir aqui.
 */
const HIDDEN_FROM_TIMELINE_AUDIT_TYPES: readonly string[] = [
  DSAttendanceAuditType.ENTRY_SHADOW_SCORE
]

/**
 * Mapeia a trilha de auditoria inteira para eventos de histórico, filtrando antes do
 * mapeamento os tipos internos (`HIDDEN_FROM_TIMELINE_AUDIT_TYPES`) — assim `isPrimary`
 * (index === 0) recai sobre o primeiro evento realmente exibido, não sobre a posição
 * bruta na resposta da API. Ponto único de consumo para os três lugares que hoje
 * chamam `.map(mapAuditToHistoryEvent)` (stationDetailsModal.vue,
 * DsAttendanceAwaitingShipment.vue, DsAttendanceAwaitingAudit.vue).
 */
export function mapAuditTrailToHistoryEvents(items: AuditTrailItemLike[]): HistoryEvent[] {
  return items
    .filter((item) => !HIDDEN_FROM_TIMELINE_AUDIT_TYPES.includes(item.actionType))
    .map(mapAuditToHistoryEvent)
}
